/**
 * Exact decimal arithmetic on strings.
 *
 * `format.ts` renders money; this file adds it up. Neither widens a value to a
 * JavaScript number, and for a voucher form that is not fastidiousness: the
 * balance invariant on `vouchers` is a `DEFERRABLE INITIALLY DEFERRED`
 * constraint trigger comparing `SUM(debit_base)` with `SUM(credit_base)` in
 * `NUMERIC(19,4)`. A form that decides "these two sides are equal" in floating
 * point holds a different opinion of equality than the trigger that will reject
 * the voucher at `COMMIT`, and the accountant is the one who finds out.
 *
 * Values are scaled to `BigInt`, so precision is bounded by memory rather than
 * by 2^53. Results keep their natural scale — `12.5 + 0.25` is `'12.75'`, and
 * `1.5 x 278.4` is `'417.60'`, not `'417.6'`. Round once, at the edge, with
 * `roundDecimalString`.
 */

/** Optional sign, then digits with an optional single point. No exponents: a
 *  `NUMERIC` never arrives as `1e6`, and accepting it would silently widen. */
const DECIMAL = /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;

/** Whether a string is safe to hand to any function in this module. */
export function isDecimal(value: string | null | undefined): boolean {
  return typeof value === 'string' && DECIMAL.test(value.trim());
}

interface Scaled {
  /** The value with its point removed, e.g. 12.75 → 1275n. */
  units: bigint;
  /** How many digits the point was moved by, e.g. 12.75 → 2. */
  scale: number;
}

function scale(value: string): Scaled {
  const text = value.trim();
  if (!DECIMAL.test(text)) {
    throw new RangeError(`Not a decimal string: ${JSON.stringify(value)}`);
  }

  const negative = text.startsWith('-');
  const body = negative ? text.slice(1) : text;
  const [int = '', frac = ''] = body.split('.');
  const units = BigInt(`${int || '0'}${frac}`);

  return { units: negative ? -units : units, scale: frac.length };
}

function unscale({ units, scale: dp }: Scaled): string {
  const negative = units < 0n;
  const digits = (negative ? -units : units).toString().padStart(dp + 1, '0');
  const int = digits.slice(0, digits.length - dp);
  const frac = dp > 0 ? `.${digits.slice(digits.length - dp)}` : '';
  const body = `${int}${frac}`;

  // -0 and -0.0000 are zero. Signing them makes `sideOf` read a credit.
  return negative && units !== 0n ? `-${body}` : body;
}

/** Lifts both operands to their common scale so they can be compared as integers. */
function common(a: Scaled, b: Scaled): [bigint, bigint, number] {
  const dp = Math.max(a.scale, b.scale);
  return [a.units * 10n ** BigInt(dp - a.scale), b.units * 10n ** BigInt(dp - b.scale), dp];
}

export function addDecimal(a: string, b: string): string {
  const [x, y, dp] = common(scale(a), scale(b));
  return unscale({ units: x + y, scale: dp });
}

export function subDecimal(a: string, b: string): string {
  const [x, y, dp] = common(scale(a), scale(b));
  return unscale({ units: x - y, scale: dp });
}

/** `'0'` for an empty list, so a voucher with no lines totals to zero. */
export function sumDecimal(values: Iterable<string>): string {
  let total = '0';
  for (const value of values) total = addDecimal(total, value);
  return total;
}

/**
 * Exact product. The scale of the result is the sum of the operands' scales,
 * which is what PostgreSQL's `numeric` multiplication does — so
 * `mulDecimal(amount, rate)` then `roundDecimalString(_, 4)` reproduces
 * `to_base(amount, rate)` digit for digit.
 */
export function mulDecimal(a: string, b: string): string {
  const x = scale(a);
  const y = scale(b);
  return unscale({ units: x.units * y.units, scale: x.scale + y.scale });
}

/**
 * Quotient to `dp` decimal places, half-up, or `null` when `b` is zero.
 *
 * **Division is the one operation here that cannot be exact**, and the return
 * type says so: `1/3` has no decimal representation at any scale, so unlike
 * `addDecimal` and `mulDecimal` this rounds and the caller is choosing where.
 * Use it only where an approximation is what is wanted and is labelled as one —
 * a courtesy "≈ USD at today's rate" beside a figure the ledger keeps in base.
 * **Never** to derive an amount that will be posted: `base = txn × rate` is a
 * multiplication in that direction precisely so nothing has to divide.
 *
 * `null` for a zero divisor rather than a throw or an Infinity: a currency whose
 * rate has not been set yet is an ordinary state of the register, and the
 * callers render "no rate yet" for it.
 */
export function divDecimal(a: string, b: string, dp = 4): string | null {
  const [x, y] = common(scale(a), scale(b));
  if (y === 0n) return null;

  // Scale the numerator by 10^(dp+1) so there is one guard digit to round on,
  // and do the half-up by hand: BigInt division truncates toward zero.
  const negative = x < 0n !== y < 0n;
  const scaled = (x < 0n ? -x : x) * 10n ** BigInt(dp + 1);
  const quotient = scaled / (y < 0n ? -y : y);
  const rounded = quotient / 10n + (quotient % 10n >= 5n ? 1n : 0n);

  return unscale({ units: negative ? -rounded : rounded, scale: dp });
}

export function cmpDecimal(a: string, b: string): -1 | 0 | 1 {
  const [x, y] = common(scale(a), scale(b));
  return x < y ? -1 : x > y ? 1 : 0;
}

export function isZeroDecimal(value: string): boolean {
  return scale(value).units === 0n;
}

export function isNegativeDecimal(value: string): boolean {
  return scale(value).units < 0n;
}

export function absDecimal(value: string): string {
  const { units, scale: dp } = scale(value);
  return unscale({ units: units < 0n ? -units : units, scale: dp });
}

export function negateDecimal(value: string): string {
  const { units, scale: dp } = scale(value);
  return unscale({ units: -units, scale: dp });
}
